import {
  Breadcrumb,
  InfoLabel,
  Modal,
  ProgressIndicator
} from '@/components/atoms';
import {
  AccordionItem,
  ImageUploadField,
  PageHeader,
  SaveCancelFooter
} from '@/components/molecules';
import {MediaPreviewWrapper, PageContainer} from '@/components/organisms';
import {
  MEDIA_PREVIEW_BANNER,
  MEDIA_PREVIEW_CARD,
  MEDIA_PREVIEW_CONFIG
} from '@/components/organisms/MediaPreview/mediaPreviewConfig';
import {SalesTargetDisplayInformationSection} from '@/containers/DisplayContent/SalesTargetDisplayInformationSection';
import {
  SALES_TARGET_SIMPLE_IMAGE_MAX_SIZE_BYTES,
  SALES_TARGET_SIMPLE_IMAGE_TYPES,
  buildTargetImageDescription
} from '@/containers/DisplayContent/salesTargetSimpleDisplayShared';
import {useSalesTargetSimpleDisplayContent} from '@/hooks/useSalesTargetSimpleDisplayContent';
import {ROUTES} from '@/utils/constants/routesConstants';
import {MODAL_TYPE, FONT_SIZE} from '@/utils/constants/globalStyle';
import {TARGET_TYPES} from '@/utils/constants/saleTargetConstants';
import '@/containers/DisplayContent/salesTargetDisplayContentV2.scss';
import './cdaSuperTargetDisplayContent.scss';

const TARGET_IMAGE_DIMENSIONS = {width: 400, height: 170};
const TARGET_IMAGE_DESCRIPTION = buildTargetImageDescription(
  TARGET_IMAGE_DIMENSIONS,
  SALES_TARGET_SIMPLE_IMAGE_MAX_SIZE_BYTES
);
const PREVIEW_SCALE = 0.3;
const PREVIEW_MIN_HEIGHT =
  MEDIA_PREVIEW_CONFIG[MEDIA_PREVIEW_BANNER].height * PREVIEW_SCALE + 56;

export const CdaSuperTargetDisplayContent = () => {
  const {
    languages,
    previewImage,
    displayContent,
    isCancel,
    imageExpanded,
    setImageExpanded,
    disableDraft,
    stepConfig,
    handleImageChange,
    handleImageDelete,
    handleContentChange,
    handleCancel,
    onConfirmCancel,
    handleDraftAction,
    handleNextAction,
    handleStepClick,
    getFormError
  } = useSalesTargetSimpleDisplayContent({
    targetType: TARGET_TYPES.CdaSuperTarget,
    draftErrorMessage: 'Error saving CDA Super Target display draft:'
  });

  return (
    <>
      <Breadcrumb
        uiSchema={{
          isDark: true,
          className: 'mb-5',
          breadcrumbs: [
            {href: ROUTES.HOME, label: ''},
            {href: ROUTES.SALES_TARGETS, label: 'Loyalty Campaigns'},
            {href: ROUTES.SALES_TARGETS, label: 'Add Sales Target'}
          ]
        }}
        id='cda_display_breadcrumb'
      />

      <PageHeader title='Add Sales Target' />

      <ProgressIndicator
        currentStep={2}
        completed={false}
        orientation='horizontal'
        steps={stepConfig.steps}
        titles={stepConfig.titles}
        className='mb-6'
        onStepClick={handleStepClick}
      />
      <br />

      <PageContainer
        data-testid='cda-super-target-media'
        className='cda-super-target-media sales-target-v2-media shadow pc-block mt-4 flex w-full flex-col rounded-lg p-4 text-white'>
        <AccordionItem
          presentation='no-background'
          isDark
          expanded={imageExpanded}
          index={0}
          label='Display Image'
          onUpdate={() => setImageExpanded((prev) => !prev)}>
          <div className='w-full pt-6'>
            <h3 className='mb-1 text-base font-bold'>Target Image*</h3>
            <p className='mb-4 text-sm text-[color:var(--color-text-secondary)]'>
              Upload Image
            </p>

            <div
              className='mb-4 grid items-start gap-6 rounded-lg border border-[color:var(--color-border-default)] p-5 md:grid-cols-2'
              data-testid='target-image-row'>
              <div className='sales-target-v2-upload cda-target-image-upload w-full min-w-0'>
                <ImageUploadField
                  label='Upload file'
                  tooltipText={TARGET_IMAGE_DESCRIPTION}
                  description={TARGET_IMAGE_DESCRIPTION}
                  imageUri={previewImage}
                  acceptedTypes={SALES_TARGET_SIMPLE_IMAGE_TYPES}
                  dimensions={TARGET_IMAGE_DIMENSIONS}
                  aspectRatio={
                    TARGET_IMAGE_DIMENSIONS.width /
                    TARGET_IMAGE_DIMENSIONS.height
                  }
                  aspectRatioLabel={`${TARGET_IMAGE_DIMENSIONS.width} x ${TARGET_IMAGE_DIMENSIONS.height}`}
                  maxSizeBytes={SALES_TARGET_SIMPLE_IMAGE_MAX_SIZE_BYTES}
                  onChange={handleImageChange}
                  onDelete={handleImageDelete}
                  dataTestId='target-image-upload'
                  containerClassName='cda-target-image-upload-field w-full'
                />
                <div className='mt-3 flex flex-wrap gap-3'>
                  <InfoLabel title='No text in the image' />
                </div>
              </div>
              <div className='cda-super-target-preview min-w-0 md:border-l md:border-[color:var(--color-border-default)] md:pl-6'>
                <p className='mb-3 text-sm font-bold'>Image Preview in App</p>
                <div
                  className='cda-super-target-preview-frame'
                  style={{minHeight: PREVIEW_MIN_HEIGHT}}
                  data-testid='cda-preview-frame'>
                  <MediaPreviewWrapper
                    types={[MEDIA_PREVIEW_BANNER, MEDIA_PREVIEW_CARD]}
                    scale={PREVIEW_SCALE}
                    content={[{image: previewImage || undefined}]}
                  />
                </div>
              </div>
            </div>
          </div>
        </AccordionItem>
      </PageContainer>

      <PageContainer data-testid='cda-super-target-display-content'>
        <PageHeader title='Display Information' fontSize={FONT_SIZE.LG} />
        <SalesTargetDisplayInformationSection
          languages={languages}
          content={displayContent}
          onContentChange={handleContentChange}
          targetTypeLabel={TARGET_TYPES.CdaSuperTarget}
          className='mt-2'
          targetTypeLabelClassName='mb-2 text-sm font-bold text-[color:var(--color-text-secondary)]'
          targetTypeValueClassName='mb-8 text-base font-semibold text-white'
        />
      </PageContainer>

      {isCancel && (
        <Modal
          type={MODAL_TYPE.FLUID}
          showModal={isCancel}
          onCloseModal={handleCancel}
          title='Are you sure you want to cancel?'
          text='Your work will not be saved if you click Yes. To continue editing and go back to the page, click NO'
          showPrimaryButton
          primaryButtonText='Yes'
          onClickPrimaryButton={onConfirmCancel}
          showSecondaryButton
          secondaryButtonText='No'
          onClickSecondaryButton={handleCancel}
          footerProps={{size: 'medium', type: 'primary'}}
          isDark
        />
      )}

      <SaveCancelFooter
        FormError={getFormError()}
        handleCancel={handleCancel}
        draftAction={handleDraftAction}
        nextAction={handleNextAction}
        disableDraft={disableDraft}
      />
    </>
  );
};
